import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { marketAPI } from '../services/api'
import { practiceAPI, practiceError } from '../services/practiceApi'
import type {
  PracticePosition,
  PracticeTrade,
  PracticeWallet,
  TutorialProgress,
} from '../types/practice'
import type { TickerQuote } from '../types'
import Spinner from '../components/Spinner'
import { formatPrice } from '../utils/marketPrice'
import { displaySymbol } from '../utils/displaySymbol'
import {
  computeLiquidationPrice,
  computeNotional,
  computeQuantity,
  LEVERAGE_CHOICES,
  type Direction,
} from '../utils/margin'

const POLL_MS = 15_000
const DEFAULT_SYMBOL = 'BTC/USDT'

// The persistent disclaimer that rides on every practice screen.
const DISCLAIMER =
  'Learning tool, not financial advice. Margin can lose more than you put in.'

const usd = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// formatPrice keeps sub-cent coins (PEPE, SHIB) readable instead of "$0".
const price = (v: number | null | undefined) => (v == null ? '—' : `$${formatPrice(v)}`)

const pnlClass = (v: number | null | undefined) =>
  v == null ? 'text-gray-300' : v > 0 ? 'text-success-500' : v < 0 ? 'text-danger-500' : 'text-gray-300'

// ---------------------------------------------------------------- shared UI

function NotRealMoneyBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-practice/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-practice-soft ring-1 ring-practice/40">
      <span aria-hidden>●</span> NOT REAL MONEY
    </span>
  )
}

function Disclaimer() {
  return <p className="mt-2 text-xs text-gray-500">{DISCLAIMER}</p>
}

/** Target / current / liquidation proximity bar. */
function LiquidationBar({ position }: { position: PracticePosition }) {
  const prox = position.liquidation_proximity
  if (!prox) return null
  const frac = Math.max(0, Math.min(prox.fraction_to_liquidation, 1))
  return (
    <div className="mt-3">
      <div className="flex justify-between text-[11px] text-gray-400">
        <span>Entry {price(prox.entry_price)}</span>
        {prox.target_price ? <span className="text-success-500">Target {price(prox.target_price)}</span> : null}
        <span className="text-danger-500">Liq {price(prox.liquidation_price)}</span>
      </div>
      <div className="relative mt-1 h-2 rounded-full bg-terminal-raised">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-practice to-danger-500"
          style={{ width: `${frac * 100}%` }}
        />
        <div
          className="absolute -top-0.5 h-3 w-1 rounded bg-white"
          style={{ left: `calc(${frac * 100}% - 2px)` }}
          title="current price"
        />
      </div>
      <div className="mt-1 text-[11px] text-gray-400">
        {prox.distance_to_liquidation_pct != null
          ? `${prox.distance_to_liquidation_pct.toFixed(1)}% from liquidation`
          : ''}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- wallet card

function WalletCard({
  wallet,
  onReset,
  resetting,
}: {
  wallet: PracticeWallet | null
  onReset: () => void
  resetting: boolean
}) {
  return (
    <div className="rounded-lg border border-practice/30 bg-terminal-surface p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-practice-soft">Virtual wallet</h2>
        <NotRealMoneyBadge />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Balance" value={usd(wallet?.balance)} />
        <Stat label="Available" value={usd(wallet?.available)} />
        <Stat label="In positions" value={usd(wallet?.collateral_in_positions)} />
        <Stat
          label="Equity (live)"
          value={usd(wallet?.equity)}
          hint={wallet?.unrealized_pnl != null ? `${usd(wallet.unrealized_pnl)} unrealized` : undefined}
        />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className={`text-sm ${pnlClass(wallet?.realized_pnl)}`}>
          Realized P&L: {usd(wallet?.realized_pnl)}
        </span>
        <button
          onClick={onReset}
          disabled={resetting}
          className="rounded-md border border-practice/40 px-3 py-1.5 text-xs font-medium text-practice-soft hover:bg-practice/10 disabled:opacity-50"
        >
          {resetting ? 'Resetting…' : 'Reset to $10,000'}
        </button>
      </div>
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-gray-100">{value}</div>
      {hint ? <div className="text-[11px] text-gray-400">{hint}</div> : null}
    </div>
  )
}

// ------------------------------------------------------------- open position

function OpenPositionForm({
  symbols,
  priceFor,
  onOpened,
  available,
}: {
  symbols: TickerQuote[]
  priceFor: (symbol: string) => number | undefined
  onOpened: () => void
  available: number | undefined
}) {
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL)
  const [direction, setDirection] = useState<Direction>('long')
  const [leverage, setLeverage] = useState(2)
  const [collateral, setCollateral] = useState(1000)
  const [submitting, setSubmitting] = useState(false)

  const ref = priceFor(symbol)
  const liq = ref ? computeLiquidationPrice(ref, leverage, direction) : null
  const notional = computeNotional(collateral, leverage)
  const qty = ref ? computeQuantity(collateral, leverage, ref) : 0

  const submit = async () => {
    setSubmitting(true)
    try {
      await practiceAPI.open({ symbol, direction, leverage, collateral, opened_from: 'symbol' })
      toast.success('Practice position opened (virtual)')
      onOpened()
    } catch (e) {
      toast.error(practiceError(e, 'Could not open the position'))
    } finally {
      setSubmitting(false)
    }
  }

  const tooMuch = available != null && collateral > available

  return (
    <div className="rounded-lg border border-terminal-border bg-terminal-surface p-5">
      <h2 className="text-sm font-semibold text-practice-soft">Open a practice position</h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">Market</span>
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="mt-1 w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 text-sm text-gray-100"
          >
            {(symbols.length ? symbols : [{ symbol: DEFAULT_SYMBOL, price: 0, change_24h_pct: null }]).map((t) => (
              <option key={t.symbol} value={t.symbol}>
                {displaySymbol(t.symbol)} {priceFor(t.symbol) ? `· ${price(priceFor(t.symbol))}` : ''}
              </option>
            ))}
          </select>
        </label>

        <div>
          <span className="text-[11px] uppercase tracking-wide text-gray-500">Direction</span>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {(['long', 'short'] as Direction[]).map((d) => (
              <button
                key={d}
                onClick={() => setDirection(d)}
                className={`rounded-md px-3 py-2 text-sm font-medium capitalize ${
                  direction === d
                    ? d === 'long'
                      ? 'bg-success-500 text-terminal-bg'
                      : 'bg-danger-500 text-white'
                    : 'border border-terminal-border text-gray-300'
                }`}
              >
                {d} {d === 'short' ? '(wins if price falls)' : '(wins if price rises)'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="text-[11px] uppercase tracking-wide text-gray-500">Leverage</span>
          <div className="mt-1 flex gap-2">
            {LEVERAGE_CHOICES.map((l) => (
              <button
                key={l}
                onClick={() => setLeverage(l)}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold ${
                  leverage === l ? 'bg-practice text-white' : 'border border-terminal-border text-gray-300'
                }`}
              >
                {l}×
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">Collateral (virtual USD)</span>
          <input
            type="number"
            min={1}
            value={collateral}
            onChange={(e) => setCollateral(Math.max(0, Number(e.target.value)))}
            className="mt-1 w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 text-sm text-gray-100"
          />
        </label>
      </div>

      {/* Live preview — the mechanics, before anything is opened. */}
      <div className="mt-4 grid grid-cols-2 gap-3 rounded-md bg-terminal-bg p-3 sm:grid-cols-4">
        <Preview label="Position size" value={usd(notional)} />
        <Preview label="Quantity" value={qty ? qty.toFixed(6) : '—'} />
        <Preview
          label="Liquidation price"
          value={liq != null ? price(liq) : '—'}
          danger
          testid="preview-liquidation"
        />
        <Preview label="Entry (live)" value={ref ? price(ref) : '—'} />
      </div>

      <button
        onClick={submit}
        disabled={submitting || collateral <= 0 || tooMuch}
        className="mt-4 w-full rounded-md bg-practice py-2.5 text-sm font-semibold text-white hover:bg-practice-hover disabled:opacity-50"
      >
        {submitting ? 'Opening…' : tooMuch ? 'Not enough virtual balance' : `Open ${leverage}× ${direction} (virtual)`}
      </button>
      <Disclaimer />
    </div>
  )
}

function Preview({
  label,
  value,
  danger,
  testid,
}: {
  label: string
  value: string
  danger?: boolean
  testid?: string
}) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-sm font-semibold ${danger ? 'text-danger-500' : 'text-gray-100'}`}>{value}</div>
    </div>
  )
}

// --------------------------------------------------------------- position card

function PositionCard({
  position,
  onClose,
}: {
  position: PracticePosition
  onClose: (id: number) => void
}) {
  return (
    <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4">
      <div className="flex items-start justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-100">{displaySymbol(position.symbol)}</span>
          <span
            className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-bold uppercase ${
              position.direction === 'long' ? 'bg-success-500/15 text-success-500' : 'bg-danger-500/15 text-danger-500'
            }`}
          >
            {position.direction} {position.leverage}×
          </span>
        </div>
        <div className={`text-right text-sm font-semibold ${pnlClass(position.unrealized_net_pnl)}`}>
          {usd(position.unrealized_net_pnl)}
          {position.pnl_pct != null ? <span className="ml-1 text-[11px]">({position.pnl_pct.toFixed(1)}%)</span> : null}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-gray-400">
        <span>Collateral {usd(position.collateral)}</span>
        <span>Size {usd(position.notional)}</span>
        <span>Now {price(position.current_price)}</span>
      </div>

      <LiquidationBar position={position} />

      <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
        <span>Fees {usd(position.entry_fee)} · interest {usd(position.interest_accrued)}</span>
        <button
          onClick={() => onClose(position.id)}
          className="rounded-md border border-practice/40 px-3 py-1 text-xs font-medium text-practice-soft hover:bg-practice/10"
        >
          Close (virtual)
        </button>
      </div>
    </div>
  )
}

// ------------------------------------------------------------- history table

const OUTCOME_STYLE: Record<string, string> = {
  target: 'text-success-500',
  manual: 'text-gray-300',
  stop: 'text-danger-500',
  liquidated: 'text-danger-500 font-bold',
}

function HistoryTable({ trades }: { trades: PracticeTrade[] }) {
  if (!trades.length) {
    return <p className="text-sm text-gray-500">No closed practice trades yet.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2">Market</th>
            <th>Side</th>
            <th>Outcome</th>
            <th className="text-right">P&L</th>
            <th className="text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="text-gray-200">
          {trades.map((t) => (
            <tr key={t.id} className="border-t border-terminal-border">
              <td className="py-2">{displaySymbol(t.symbol)}</td>
              <td className="capitalize">
                {t.direction} {t.leverage}×
              </td>
              <td className={`capitalize ${OUTCOME_STYLE[t.outcome] || ''}`}>{t.outcome}</td>
              <td className={`text-right ${pnlClass(t.net_pnl)}`}>
                {usd(t.net_pnl)}
                {t.return_pct != null ? <span className="ml-1 text-[11px]">({t.return_pct.toFixed(1)}%)</span> : null}
              </td>
              <td className="text-right">{usd(t.resulting_balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ------------------------------------------------------------------ tutorial

type StepDef = { title: string; body: string }

const STEPS: StepDef[] = [
  {
    title: 'This is fake money',
    body: 'Your practice wallet starts at $10,000 of virtual USD. Nothing here is real — no funds move, and none of this touches your signal record. Get oriented, then move on.',
  },
  {
    title: 'Place a long',
    body: 'A long buys the asset: it gains when the price rises and loses when it falls. Open a small long to see the mechanics.',
  },
  {
    title: 'Place a short',
    body: 'A short is the mirror image: you sell/borrow the asset, so it wins when the price FALLS and loses when it rises. Open a small short to feel the inversion.',
  },
  {
    title: 'Get liquidated on purpose',
    body: 'Open a 5× short and drive a simulated adverse move until liquidation fires. Leverage multiplies BOTH directions. Liquidation seizes your whole collateral and closes the position — you cannot wait for a recovery. Lower leverage moves the liquidation price further away.',
  },
  {
    title: 'Leverage vs risk',
    body: 'The same trade at 1× and at 5×: higher leverage puts the liquidation price much closer to your entry, so a smaller move wipes you out.',
  },
  {
    title: 'Fees & interest',
    body: 'A leveraged position is not free to hold: a taker fee is charged when you open and close, and borrow interest accrues on the leveraged portion over time. Holding costs money.',
  },
]

function Tutorial({
  progress,
  onProgress,
  refSymbol,
  refPrice,
  onWalletChanged,
}: {
  progress: TutorialProgress | null
  onProgress: (p: TutorialProgress) => void
  refSymbol: string
  refPrice: number | undefined
  onWalletChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [liqResult, setLiqResult] = useState<string | null>(null)
  const current = progress?.current_step ?? 1
  const completed = new Set(progress?.completed_steps ?? [])

  const advance = async (step: number) => {
    try {
      const p = await practiceAPI.advanceTutorial(step)
      onProgress(p)
    } catch (e) {
      toast.error(practiceError(e))
    }
  }

  // Step 4: open a 5x short and force the adverse move until liquidation fires.
  const runLiquidationLesson = async () => {
    if (!refPrice) {
      toast.error('Waiting for a live price…')
      return
    }
    setBusy(true)
    setLiqResult(null)
    try {
      const pos = await practiceAPI.open({
        symbol: refSymbol,
        direction: 'short',
        leverage: 5,
        collateral: 200,
        opened_from: 'symbol',
      })
      // A short liquidates ABOVE entry; push just past it.
      const target = (pos.liquidation_price || refPrice * 1.2) * 1.001
      const res = await practiceAPI.refresh(pos.id, target)
      if (res.closed && res.outcome === 'liquidated') {
        setLiqResult(
          `Liquidated at ${price(pos.liquidation_price)} — the full $200 collateral was seized (net ${usd(res.trade?.net_pnl)}). That is the lesson: leverage cuts both ways and liquidation is final.`,
        )
      } else {
        setLiqResult('The move did not reach liquidation — try again.')
      }
      onWalletChanged()
    } catch (e) {
      toast.error(practiceError(e, 'Could not run the lesson'))
    } finally {
      setBusy(false)
    }
  }

  const step = STEPS[current - 1]
  const liq1x = refPrice ? computeLiquidationPrice(refPrice, 1, 'long') : null
  const liq5x = refPrice ? computeLiquidationPrice(refPrice, 5, 'long') : null

  return (
    <div className="rounded-lg border border-practice/30 bg-terminal-surface p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-practice-soft">
          Guided tutorial — mechanics only
        </h2>
        <NotRealMoneyBadge />
      </div>

      {/* step dots */}
      <div className="mt-4 flex gap-2">
        {STEPS.map((_, i) => {
          const n = i + 1
          const state = completed.has(n) ? 'done' : n === current ? 'active' : 'todo'
          return (
            <div
              key={n}
              className={`h-2 flex-1 rounded-full ${
                state === 'done' ? 'bg-practice' : state === 'active' ? 'bg-practice-soft' : 'bg-terminal-raised'
              }`}
              title={`Step ${n}`}
            />
          )
        })}
      </div>

      {progress?.tutorial_completed ? (
        <div className="mt-6 rounded-md border border-practice/40 bg-practice/10 p-4">
          <p className="text-sm font-semibold text-practice-soft">Tutorial complete ✓</p>
          <p className="mt-1 text-xs text-gray-400">
            Your completion is saved. (It is recorded for a future real-margin feature; margin
            trading does not exist on this platform yet — spot only — so nothing is unlocked now.)
          </p>
          <button
            onClick={async () => onProgress(await practiceAPI.resetTutorial())}
            className="mt-3 rounded-md border border-practice/40 px-3 py-1.5 text-xs text-practice-soft hover:bg-practice/10"
          >
            Restart tutorial
          </button>
        </div>
      ) : (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Step {current} of {STEPS.length}
          </div>
          <h3 className="mt-1 text-lg font-semibold text-gray-100">{step.title}</h3>
          <p className="mt-2 text-sm text-gray-300">{step.body}</p>

          {current === 4 && (
            <div className="mt-4">
              <button
                onClick={runLiquidationLesson}
                disabled={busy}
                className="rounded-md bg-danger-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-50"
              >
                {busy ? 'Running…' : 'Open a 5× short and drive it to liquidation'}
              </button>
              {liqResult ? <p className="mt-3 text-sm text-danger-500">{liqResult}</p> : null}
            </div>
          )}

          {current === 5 && refPrice && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-terminal-bg p-3">
                <div className="text-[11px] uppercase text-gray-500">1× long liquidation</div>
                <div className="mt-1 text-sm font-semibold text-gray-100">{price(liq1x)} · never (0)</div>
                <div className="text-[11px] text-gray-400">Effectively can't be liquidated</div>
              </div>
              <div className="rounded-md bg-terminal-bg p-3">
                <div className="text-[11px] uppercase text-gray-500">5× long liquidation</div>
                <div className="mt-1 text-sm font-semibold text-danger-500">{price(liq5x)}</div>
                <div className="text-[11px] text-gray-400">Only a 20% drop wipes you out</div>
              </div>
            </div>
          )}

          <button
            onClick={() => advance(current)}
            className="mt-5 rounded-md bg-practice px-4 py-2 text-sm font-semibold text-white hover:bg-practice-hover"
          >
            {current === STEPS.length ? 'Finish tutorial' : 'Mark step complete & continue'}
          </button>
        </div>
      )}

      <Disclaimer />
    </div>
  )
}

// ------------------------------------------------------------------ page

type View = 'simulator' | 'tutorial'

export default function Practice() {
  const [allowed, setAllowed] = useState<boolean | null>(null)
  const [view, setView] = useState<View>('simulator')
  const [wallet, setWallet] = useState<PracticeWallet | null>(null)
  const [positions, setPositions] = useState<PracticePosition[]>([])
  const [trades, setTrades] = useState<PracticeTrade[]>([])
  const [tutorial, setTutorial] = useState<TutorialProgress | null>(null)
  const [ticker, setTicker] = useState<TickerQuote[]>([])
  const [loading, setLoading] = useState(true)
  const [resetting, setResetting] = useState(false)
  const seenEvents = useRef<Set<number>>(new Set())

  const priceFor = useCallback(
    (symbol: string) => ticker.find((t) => t.symbol === symbol)?.price,
    [ticker],
  )

  const loadTicker = useCallback(async () => {
    try {
      const tape = await marketAPI.getTicker()
      setTicker(tape.tickers || [])
    } catch {
      /* keep last good prices */
    }
  }, [])

  const loadPositions = useCallback(async () => {
    try {
      const data = await practiceAPI.positions()
      setPositions(data.positions)
      setWallet(data.wallet)
      // Announce any liquidation/stop/target that fired since last poll.
      for (const ev of data.events) {
        if (!seenEvents.current.has(ev.trade.id)) {
          seenEvents.current.add(ev.trade.id)
          if (ev.outcome === 'liquidated') toast.error(`${ev.trade.symbol} was LIQUIDATED (virtual)`)
          else toast(`${displaySymbol(ev.trade.symbol)} closed: ${ev.outcome} (virtual)`)
        }
      }
      if (data.events.length) {
        setTrades((await practiceAPI.history()).trades)
      }
    } catch {
      /* keep last good state */
    }
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      setTrades((await practiceAPI.history()).trades)
    } catch {
      /* ignore */
    }
  }, [])

  const loadAll = useCallback(async () => {
    await Promise.all([loadTicker(), loadPositions(), loadHistory()])
  }, [loadTicker, loadPositions, loadHistory])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const access = await practiceAPI.access()
        if (!active) return
        setAllowed(access.allowed)
        if (access.allowed) {
          await Promise.all([
            loadAll(),
            practiceAPI.tutorial().then((t) => active && setTutorial(t)),
          ])
        }
      } catch {
        if (active) setAllowed(false)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [loadAll])

  useEffect(() => {
    if (!allowed) return
    const id = setInterval(() => {
      if (!document.hidden) {
        loadTicker()
        loadPositions()
      }
    }, POLL_MS)
    return () => clearInterval(id)
  }, [allowed, loadTicker, loadPositions])

  const onClosePosition = async (id: number) => {
    try {
      const res = await practiceAPI.close(id)
      toast.success(`Closed: ${res.outcome} · ${usd(res.trade.net_pnl)} (virtual)`)
      await loadAll()
    } catch (e) {
      toast.error(practiceError(e, 'Could not close the position'))
    }
  }

  const onReset = async () => {
    setResetting(true)
    try {
      const w = await practiceAPI.resetWallet()
      setWallet(w)
      setPositions([])
      setTrades([])
      seenEvents.current.clear()
      toast.success('Practice wallet reset to $10,000')
    } catch (e) {
      toast.error(practiceError(e))
    } finally {
      setResetting(false)
    }
  }

  const refSymbol = positions[0]?.symbol || DEFAULT_SYMBOL
  const refPrice = useMemo(() => priceFor(refSymbol) ?? priceFor(DEFAULT_SYMBOL), [priceFor, refSymbol])

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-gray-300">
        The practice simulator is currently limited to FinCreator admins.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-16">
      {/* Header — purple identity + persistent NOT REAL MONEY badge. */}
      <div className="rounded-lg border border-practice/40 bg-gradient-to-r from-practice/15 to-terminal-surface p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-practice-soft">Practice — Margin Simulator</h1>
            <p className="mt-1 text-sm text-gray-400">
              Real Kraken prices · 100% virtual money · never a real order. Separate from your signal record.
            </p>
          </div>
          <NotRealMoneyBadge />
        </div>
        <div className="mt-4 inline-flex rounded-lg border border-practice/30 p-1">
          {(['simulator', 'tutorial'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize ${
                view === v ? 'bg-practice text-white' : 'text-practice-soft hover:bg-practice/10'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === 'simulator' ? (
        <>
          <WalletCard wallet={wallet} onReset={onReset} resetting={resetting} />
          <OpenPositionForm
            symbols={ticker}
            priceFor={priceFor}
            onOpened={loadAll}
            available={wallet?.available}
          />

          <div>
            <h2 className="mb-3 text-sm font-semibold text-practice-soft">Open positions</h2>
            {positions.length ? (
              <div className="space-y-3">
                {positions.map((p) => (
                  <PositionCard key={p.id} position={p} onClose={onClosePosition} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No open practice positions.</p>
            )}
          </div>

          <div className="rounded-lg border border-terminal-border bg-terminal-surface p-5">
            <h2 className="mb-3 text-sm font-semibold text-practice-soft">
              Practice history{' '}
              <span className="text-[11px] font-normal text-gray-500">— separate from your signal record</span>
            </h2>
            <HistoryTable trades={trades} />
          </div>
        </>
      ) : (
        <Tutorial
          progress={tutorial}
          onProgress={setTutorial}
          refSymbol={refSymbol}
          refPrice={refPrice}
          onWalletChanged={loadAll}
        />
      )}
    </div>
  )
}
